import { damageReportsApi, type DamageReportDto, type DamageReportUrgency } from '../../services/damage-reports.api';
import { authorizedFetch } from '../../services/core.api';
import { onLocaleChange, t, i18nService } from '../../services/i18n';
import { buildPropertyDetailsUrl } from '../property/property-navigation';

function escapeHtml(value: string): string {
  const element = document.createElement('span');
  element.textContent = value;
  return element.innerHTML;
}

function resolveUrgency(value?: DamageReportUrgency): DamageReportUrgency {
  return value || 'Normal';
}

export class RcDamageReportListPage extends HTMLElement {
  private reports: DamageReportDto[] = [];
  private isLoading = false;
  private error = '';
  private localeCleanup?: () => void;

  connectedCallback(): void {
    this.localeCleanup = onLocaleChange(() => this.render());
    this.addEventListener('click', this.handleClick);
    void this.load();
  }

  disconnectedCallback(): void {
    this.localeCleanup?.();
    this.removeEventListener('click', this.handleClick);
  }

  private async load(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    this.render();

    try {
      this.reports = await damageReportsApi.list();
    } catch (error: any) {
      this.error = error?.message || t('damageReports.loadError');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private render(): void {
    this.innerHTML = `
      <section class="page-header">
        <div>
          <h1>${t('nav.damageReports')}</h1>
          <p class="page-subtitle">${t('damageReports.pageDescription')}</p>
        </div>
      </section>
      ${this.renderBody()}
    `;
  }

  private renderBody(): string {
    if (this.isLoading) {
      return `<div class="card"><div class="empty-state">${t('common.loading')}</div></div>`;
    }

    if (this.error) {
      return `<div class="card"><div class="form-error">${escapeHtml(this.error)}</div></div>`;
    }

    if (!this.reports.length) {
      return `<div class="card"><div class="empty-state"><p>${t('damageReports.empty')}</p></div></div>`;
    }

    return `
      <div class="card">
        <table>
          <thead>
            <tr>
              <th>${t('common.date')}</th>
              <th>${t('common.property')}</th>
              <th>${t('common.tenant')}</th>
              <th>${t('common.description')}</th>
              <th>${t('common.photos')}</th>
            </tr>
          </thead>
          <tbody>
            ${this.reports.map((report) => `
              <tr>
                <td>${escapeHtml(i18nService.formatDate(report.createdAt, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }))}</td>
                <td><a href="${escapeHtml(buildPropertyDetailsUrl(report.propertyId, { tab: 'damage-reports' }))}">${escapeHtml(report.propertyAddress)}</a></td>
                <td>${escapeHtml(report.tenantName || '-')}</td>
                <td>
                  <div class="doc-filename">${escapeHtml(t(`damageReports.urgency.${resolveUrgency(report.urgency).toLowerCase()}`))}</div>
                  <div class="text-muted text-sm">${escapeHtml(report.description)}</div>
                </td>
                <td>
                  ${report.photos.length === 0 ? '-' : `
                    <div style="display:flex;flex-direction:column;gap:0.35rem;">
                      ${report.photos.map((photo) => `
                        <button class="btn btn-outline btn-sm" data-photo-url="${escapeHtml(photo.downloadUrl)}" data-file-name="${escapeHtml(photo.fileName)}">${escapeHtml(photo.fileName)}</button>
                      `).join('')}
                    </div>
                  `}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
  }

  private handleClick = async (event: Event): Promise<void> => {
    const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-photo-url]');
    if (!button?.dataset.photoUrl) {
      return;
    }

    const response = await authorizedFetch(button.dataset.photoUrl);
    if (!response.ok) {
      return;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
}

if (!customElements.get('rc-damage-report-list-page')) {
  customElements.define('rc-damage-report-list-page', RcDamageReportListPage);
}
